import { z } from 'zod';
import { filterNode, memberName } from './query.js';
import { sectionPermissions } from './permissions.js';

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const alertWindow = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('minutes'), minutes: z.number().int().min(5).max(10080) }).strict(),
  z.object({ kind: z.literal('today') }).strict(),
  z.object({ kind: z.literal('now') }).strict(),
]);
export type AlertWindow = z.infer<typeof alertWindow>;

export const alertCondition = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('threshold'),
      operator: z.enum(['gt', 'gte', 'lt', 'lte', 'eq']),
      value: z.number(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('deviation'),
      direction: z.enum(['below', 'above']),
      percent: z.number().min(1).max(1000),
      weeks: z.number().int().min(1).max(12),
    })
    .strict(),
  z.object({ kind: z.literal('no_activity'), weeks: z.number().int().min(1).max(12) }).strict(),
]);
export type AlertCondition = z.infer<typeof alertCondition>;

export const alertDefinition = z
  .object({
    metric: memberName,
    groupBy: memberName.optional(),
    labelBy: memberName.optional(),
    filters: z.array(filterNode).max(20).default([]),
    realOnly: z.boolean().default(true),
    timeMember: memberName.optional(),
    window: alertWindow,
    condition: alertCondition,
    activeHours: z.object({ from: hhmm, to: hhmm }).strict().optional(),
    respectSchedule: z.boolean().default(false),
  })
  .strict();
export type AlertDefinition = z.input<typeof alertDefinition>;
export type ParsedAlertDefinition = z.output<typeof alertDefinition>;

export const alertChannels = ['feed', 'push'] as const;
export type AlertChannel = (typeof alertChannels)[number];

export const alertRuleInput = z
  .object({
    name: z.string().trim().min(1).max(200),
    template: z.string().max(60).nullable().default(null),
    definition: alertDefinition,
    enabled: z.boolean().default(true),
    cooldownMinutes: z.number().int().min(1).max(10080).default(60),
    notifyRecovery: z.boolean().default(true),
    channels: z.array(z.enum(alertChannels)).min(1).max(2).default(['feed', 'push']),
  })
  .strict();
export type AlertRuleInput = z.input<typeof alertRuleInput>;

export interface AlertTemplate {
  code: string;
  definition: AlertDefinition;
  cooldownMinutes: number;
}

export const alertTemplates: AlertTemplate[] = [
  {
    code: 'station_offline',
    cooldownMinutes: 60,
    definition: {
      metric: 'station_state.max_offline_minutes',
      groupBy: 'stations.station_id',
      labelBy: 'stations.short_number',
      filters: [{ member: 'station_state.is_installed', operator: 'isTrue' }],
      window: { kind: 'now' },
      condition: { kind: 'threshold', operator: 'gt', value: 30 },
      respectSchedule: true,
    },
  },
  {
    code: 'no_registrations',
    cooldownMinutes: 60,
    definition: {
      metric: 'registrations.count',
      window: { kind: 'minutes', minutes: 60 },
      condition: { kind: 'threshold', operator: 'lte', value: 0 },
      activeHours: { from: '09:00', to: '23:00' },
    },
  },
  {
    code: 'rents_below_usual',
    cooldownMinutes: 240,
    definition: {
      metric: 'rents.count',
      window: { kind: 'today' },
      condition: { kind: 'deviation', direction: 'below', percent: 30, weeks: 4 },
    },
  },
  {
    code: 'declined_charges',
    cooldownMinutes: 60,
    definition: {
      metric: 'rent_charges.declined_share',
      window: { kind: 'minutes', minutes: 60 },
      condition: { kind: 'threshold', operator: 'gt', value: 0.2 },
    },
  },
  {
    code: 'counterparty_no_payments',
    cooldownMinutes: 720,
    definition: {
      metric: 'payments.payments_count',
      groupBy: 'counterparties.counterparty_id',
      labelBy: 'counterparties.name',
      window: { kind: 'today' },
      condition: { kind: 'no_activity', weeks: 4 },
      activeHours: { from: '18:00', to: '23:59' },
    },
  },
  {
    code: 'new_debt',
    cooldownMinutes: 1440,
    definition: {
      metric: 'rents.debt_amount_total',
      groupBy: 'rents.rent_number',
      timeMember: 'rents.ended_at',
      window: { kind: 'minutes', minutes: 120 },
      condition: { kind: 'threshold', operator: 'gt', value: 1000 },
    },
  },
  {
    code: 'battery_capacity_loss',
    cooldownMinutes: 1440,
    definition: {
      metric: 'batteries.min_health',
      groupBy: 'batteries.power_bank_serial',
      labelBy: 'batteries.power_bank_serial',
      window: { kind: 'now' },
      condition: { kind: 'threshold', operator: 'lt', value: 0.7 },
    },
  },
  {
    code: 'station_empty',
    cooldownMinutes: 60,
    definition: {
      metric: 'station_state.empty_count',
      groupBy: 'stations.station_id',
      labelBy: 'stations.short_number',
      window: { kind: 'now' },
      condition: { kind: 'threshold', operator: 'gt', value: 0 },
      respectSchedule: true,
    },
  },
  {
    code: 'station_full',
    cooldownMinutes: 60,
    definition: {
      metric: 'station_state.full_count',
      groupBy: 'stations.station_id',
      labelBy: 'stations.short_number',
      window: { kind: 'now' },
      condition: { kind: 'threshold', operator: 'gt', value: 0 },
      respectSchedule: true,
    },
  },
  {
    code: 'eject_failures',
    cooldownMinutes: 60,
    definition: {
      metric: 'station_events.eject_failures',
      groupBy: 'stations.station_id',
      labelBy: 'stations.short_number',
      window: { kind: 'minutes', minutes: 60 },
      condition: { kind: 'threshold', operator: 'gte', value: 2 },
    },
  },
  {
    code: 'revenue_below_usual',
    cooldownMinutes: 240,
    definition: {
      metric: 'money.revenue',
      window: { kind: 'today' },
      condition: { kind: 'deviation', direction: 'below', percent: 30, weeks: 4 },
      activeHours: { from: '12:00', to: '23:59' },
    },
  },
  {
    code: 'paid_without_rent',
    cooldownMinutes: 180,
    definition: {
      metric: 'reconciliation.paid_without_rent_count',
      window: { kind: 'minutes', minutes: 120 },
      condition: { kind: 'threshold', operator: 'gt', value: 0 },
    },
  },
];

export const systemAlertCodes = ['backup_failed', 'backup_stale', 'backup_dir'] as const;
export type SystemAlertCode = (typeof systemAlertCodes)[number];

export const systemAlertAudience: Record<SystemAlertCode, string> = {
  backup_failed: sectionPermissions.settings,
  backup_stale: sectionPermissions.settings,
  backup_dir: sectionPermissions.settings,
};

export const systemAlertAudiences: string[] = [...new Set(Object.values(systemAlertAudience))];

export const backupStaleHours = 26;

export const systemAlertRenotifyMinutes = 24 * 60;

export function isSystemAlertCode(value: unknown): value is SystemAlertCode {
  return typeof value === 'string' && (systemAlertCodes as readonly string[]).includes(value);
}

export type SystemAlertKind = 'fire' | 'recover';

export interface SystemAlertText {
  key: string;
  values?: Record<string, number | string | { format: 'datetime'; value: string | null }>;
}

function param(params: Record<string, unknown>, key: string): string {
  const value = params[key];
  return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
}

function count(params: Record<string, unknown>, key: string): number {
  const value = Number(params[key]);
  return Number.isFinite(value) ? value : 0;
}

export function systemAlertText(
  code: SystemAlertCode,
  kind: SystemAlertKind,
  params: Record<string, unknown>,
): { title: SystemAlertText; body: SystemAlertText } {
  const title = { key: `alerts.system.${code}.title` };
  if (kind === 'recover') {
    const at = param(params, 'at');
    return {
      title,
      body: at
        ? { key: `alerts.system.${code}.recover`, values: { time: { format: 'datetime', value: at } } }
        : { key: `alerts.system.${code}.recoverNow` },
    };
  }
  switch (code) {
    case 'backup_failed':
      return {
        title,
        body: {
          key: 'alerts.system.backup_failed.fire',
          values: { attempts: count(params, 'attempts'), error: param(params, 'error').slice(0, 300) },
        },
      };
    case 'backup_stale': {
      const last = param(params, 'lastSuccessAt');
      return {
        title,
        body: last
          ? {
              key: 'alerts.system.backup_stale.fire',
              values: { time: { format: 'datetime', value: last }, hours: count(params, 'hours') },
            }
          : { key: 'alerts.system.backup_stale.fireNever', values: { hours: count(params, 'hours') } },
      };
    }
    case 'backup_dir':
      return {
        title,
        body: {
          key: 'alerts.system.backup_dir.fire',
          values: { dir: param(params, 'dir'), error: param(params, 'error').slice(0, 300) },
        },
      };
  }
}
